/**
 * MissionsSection - Quests/Missions Display
 *
 * Renders the EXACT same layout as the ChallengePage quest section.
 * Uses the same components (QuestCard, GameQuestSection) and CSS classes.
 */

import { useMemo, useState } from "react";
import { FaBullseye, FaSpinner, FaUserPlus } from "react-icons/fa";
import { IoIosCamera } from "react-icons/io";
import { useGetChallenge } from "@/hooks/challenges/useGetChallenge";
import { ChallengeInfoResponse, QuestConfigMilestone, MilestoneSubtype } from "@devour/client";
import { getIconForUrl } from "@/utils/socialDeepLinks";
import { ChallengeLoginModal } from "@/components/modals/ChallengeLoginModal";
import { InviteModal, InviteModalType } from "@/components/InviteModal";
import { QuestCard, GameQuestSection } from "@/pages/challenge/ChallengePage";
import ChallengePromotionsSection from "@/pages/challenge/ChallengePromotionsSection";
import { ChallengeFAQ } from "@/pages/challenge/ChallengeFAQ";
import "./MissionsSection.scss";

// Types from ChallengePage
interface CardTrackerColorConfig {
    subtype: "GAME" | "PHOTO" | "SOCIAL" | "WEBHOOK" | "REFERRAL";
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    accentColor?: string;
    iconColor?: string;
}

interface QuestsSectionConfig {
    backgroundColor?: string;
    textColor?: string;
    accentColor?: string;
    cardTrackers?: CardTrackerColorConfig[];
}

interface ChallengeWithEntriesStyles extends ChallengeInfoResponse {
    questsStyles?: QuestsSectionConfig;
}

/** Extended challenge type that includes progressions from the API */
type ChallengeWithProgressions = ChallengeInfoResponse & {
    progressions?: Array<{
        questConfigId: string;
        progression: Record<string, { amount?: number; isComplete?: boolean; claimedAt?: string }>;
    }>;
};

interface MissionsSectionProps {
    challengeId?: string;
    /** Brand name from Hub (fallback when challenge.brandName is empty) */
    hubBrandName?: string;
    /** Brand color from Hub theme (fallback when challenge.brandColor is empty) */
    hubBrandColor?: string;
}

export const MissionsSection = ({ challengeId, hubBrandName, hubBrandColor }: MissionsSectionProps) => {
    // Challenge-based
    const { data: challenge, isLoading, error, refetch } = useGetChallenge(challengeId);

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);

    const handleLoginRequired = () => setShowLoginModal(true);
    const handleLoginClose = () => {
        setShowLoginModal(false);
        setTimeout(() => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "0";
        }, 300);
    };

    // Build CSS variables from challenge theme colors (same logic as ChallengePage)
    const cssVariables = useMemo(() => {
        if (!challenge) return {};
        const themeColors = challenge.themeColors;
        const brandColor = challenge.brandColor;

        const hasValue = (val: string | undefined): val is string => Boolean(val && val.trim());
        const primaryColor = hasValue(themeColors?.primary) ? themeColors.primary : hasValue(brandColor) ? brandColor : undefined;
        const secondaryColor = hasValue(themeColors?.secondary) ? themeColors.secondary : hasValue(brandColor) ? brandColor : undefined;

        return {
            ...primaryColor && { "--challenge-primary": primaryColor },
            ...(themeColors?.primaryDark || primaryColor) && { "--challenge-primary-dark": themeColors?.primaryDark || primaryColor },
            ...secondaryColor && { "--challenge-secondary": secondaryColor },
            ...(themeColors?.secondaryDark || secondaryColor) && { "--challenge-secondary-dark": themeColors?.secondaryDark || secondaryColor },
            ...(themeColors?.accent || primaryColor) && { "--challenge-accent": themeColors?.accent || primaryColor },
            ...hasValue(themeColors?.background) && { "--challenge-background": themeColors.background },
            ...hasValue(themeColors?.cardBackground) && { "--challenge-card-background": themeColors.cardBackground },
            ...hasValue(themeColors?.textPrimary) && { "--challenge-text-primary": themeColors.textPrimary },
            ...hasValue(themeColors?.textSecondary) && { "--challenge-text-secondary": themeColors.textSecondary },
            ...hasValue(themeColors?.success) && { "--challenge-success": themeColors.success },
            ...hasValue(themeColors?.error) && { "--challenge-error": themeColors.error },
        } as React.CSSProperties;
    }, [challenge]);

    // Get active segments (for milestones)
    const activeSegments = useMemo(() => {
        const segments = challenge?.segments ?? [];
        const now = Date.now();
        return segments.filter(s => now >= s.startAt && now <= s.endAt);
    }, [challenge?.segments]);

    // Get past segments (already ended)
    const pastSegments = useMemo(() => {
        const segments = challenge?.segments ?? [];
        const now = Date.now();
        return segments.filter(s => now > s.endAt);
    }, [challenge?.segments]);

    // Build progressionMap from ALL segments
    const progressionMap = useMemo(() => {
        const map: Record<string, { amount?: number; isComplete?: boolean; claimedAt?: number }> = {};
        const allSegments = challenge?.segments ?? [];
        const challengeWithProgressions = challenge as unknown as ChallengeWithProgressions | undefined;
        allSegments.forEach(segment => {
            const progression = challengeWithProgressions?.progressions?.find(p => p.questConfigId === segment.mainQuestId);
            if (progression?.progression) {
                Object.entries(progression.progression).forEach(([milestoneId, data]) => {
                    map[milestoneId] = data;
                });
            }
        });
        return map;
    }, [challenge?.segments, challenge]);

    // Combine milestones: all from active segments + CLAIMED ones from past segments
    const currentMilestones = useMemo(() => {
        const activeMilestones = activeSegments.flatMap(segment => segment.mainQuest?.milestones ?? []);
        const claimedPastMilestones = pastSegments.flatMap(segment => {
            const milestones = segment.mainQuest?.milestones ?? [];
            return milestones.filter(m => {
                const prog = progressionMap[m.id];
                return prog?.isComplete && prog?.claimedAt;
            });
        });

        const seen = new Set<string>();
        return [...activeMilestones, ...claimedPastMilestones].filter(m => {
            if (seen.has(m.id)) return false;
            seen.add(m.id);
            return true;
        });
    }, [activeSegments, pastSegments, progressionMap]);

    // Separate milestones by subtype: PHOTO, SOCIAL, GAME, REFERRAL
    const { photoMilestones, socialMilestones, referralMilestones, gameMilestonesByGame } = useMemo(() => {
        const photo: QuestConfigMilestone[] = [];
        const social: QuestConfigMilestone[] = [];
        const referral: QuestConfigMilestone[] = [];
        const game: Record<string, QuestConfigMilestone[]> = {};

        for (const milestone of currentMilestones) {
            const subtype = milestone.subtype;

            if (subtype === MilestoneSubtype.PHOTO) {
                photo.push(milestone);
            } else if (subtype === MilestoneSubtype.SOCIAL) {
                social.push(milestone);
            } else if (subtype === MilestoneSubtype.REFERRAL) {
                referral.push(milestone);
            } else if (subtype === MilestoneSubtype.GAME) {
                const gameId = milestone.gameId || "other";
                if (!game[gameId]) {
                    game[gameId] = [];
                }
                game[gameId].push(milestone);
            } else if (!subtype) {
                // Legacy: determine by URL presence if no subtype set
                if (milestone.url) {
                    social.push(milestone);
                } else {
                    photo.push(milestone);
                }
            }
        }

        return { photoMilestones: photo, socialMilestones: social, referralMilestones: referral, gameMilestonesByGame: game };
    }, [currentMilestones]);

    const hasGameMilestones = Object.keys(gameMilestonesByGame).length > 0;

    // Challenge with dates for QuestCard
    const challengeWithDates = useMemo(() => {
        if (!challenge) return challenge;
        if (!challenge.segments?.length) return challenge;
        const starts = challenge.segments.map(s => s.startAt).filter(Boolean);
        const ends = challenge.segments.map(s => s.endAt).filter(Boolean);
        if (!starts.length || !ends.length) return challenge;
        return {
            ...challenge,
            start: Math.min(...starts),
            end: Math.max(...ends),
        };
    }, [challenge]);

    // ── No challengeId: static placeholder ──
    if (!challengeId) {
        return (
            <section className="section missions-section">
                <div className="section__empty">
                    <FaBullseye size={48} />
                    <h3>Missions Coming Soon</h3>
                    <p>Complete missions to earn XP and unlock rewards.</p>
                </div>
            </section>
        );
    }

    // ── Legacy Mode: challenge-based rendering ──
    if (isLoading) {
        return (
            <section className="section missions-section missions-section--loading">
                <div className="section__loading">
                    <FaSpinner className="spin" size={32} />
                    <span>Loading missions...</span>
                </div>
            </section>
        );
    }

    if (error || !challenge) {
        return (
            <section className="section missions-section">
                <div className="section__empty">
                    <FaBullseye size={48} />
                    <h3>Missions Unavailable</h3>
                    <p>Unable to load missions. Please try again later.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="section missions-section">
            {/* Modals */}
            <ChallengeLoginModal
                challenge={challenge}
                isOpen={showLoginModal}
                onClose={handleLoginClose}
                title="Sign in to continue"
                description="To track your challenge progress, you will need a DevourPlay account."
            />
            <InviteModal
                type={InviteModalType.CHALLENGE}
                challengeId={challenge.id}
                isVisible={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                alwaysUseDarkBackground={true}
                brandColorOverride={hubBrandColor}
            />

            {/* Challenge page wrapper for CSS context */}
            <div className="challenge-page missions-section__challenge-wrapper" style={cssVariables}>
                {/* Main content - single column for Brand Hub */}
                <div className="challenge-page_main-content missions-section__single-column">
                    {/* Quest entry info */}
                    <div className="challenge-page_quest-entry-info">
                        <div className="challenge-page_quest-entry-info_text">
                            <div className="challenge-page_quest-entry-info_text_title">
                                THIS WEEK'S MISSIONS
                            </div>
                        </div>

                        {/* Photo quests - full width layout */}
                        {photoMilestones.length > 0 &&
                            <div className="challenge-page_quest-entry-info_rows">
                                {photoMilestones
                                    .sort((a, b) => {
                                        const aClaimed = progressionMap[a.id]?.isComplete && progressionMap[a.id]?.claimedAt ? 1 : 0;
                                        const bClaimed = progressionMap[b.id]?.isComplete && progressionMap[b.id]?.claimedAt ? 1 : 0;
                                        return aClaimed - bClaimed;
                                    })
                                    .map((milestone, index) => {
                                        const progression = progressionMap[milestone.id];
                                        const photoTrackerColors = (challenge as ChallengeWithEntriesStyles)?.questsStyles?.cardTrackers?.find(t => t.subtype === "PHOTO");
                                        return (
                                            <QuestCard
                                                key={index}
                                                refetch={refetch}
                                                refetchChallenge={refetch}
                                                label={milestone.name || "Complete the Challenge"}
                                                icon={<IoIosCamera size={48} />}
                                                type="filled"
                                                milestone={milestone}
                                                isComplete={progression?.isComplete ?? false}
                                                isClaimed={!!(progression?.isComplete && progression?.claimedAt)}
                                                isAccepted={challenge?.isAccepted ?? false}
                                                challenge={challengeWithDates}
                                                onLoginRequired={handleLoginRequired}
                                                trackerColors={photoTrackerColors}
                                                brandNameOverride={hubBrandName}
                                                brandColorOverride={hubBrandColor}
                                            />
                                        );
                                    })}
                            </div>
                        }

                        {/* Social quests - 2 column layout */}
                        {socialMilestones.length > 0 &&
                            <div className="challenge-page_quest-entry-info_columns">
                                {socialMilestones
                                    .sort((a, b) => {
                                        const aClaimed = progressionMap[a.id]?.isComplete && progressionMap[a.id]?.claimedAt ? 1 : 0;
                                        const bClaimed = progressionMap[b.id]?.isComplete && progressionMap[b.id]?.claimedAt ? 1 : 0;
                                        return aClaimed - bClaimed;
                                    })
                                    .map((milestone, index) => {
                                        const progression = progressionMap[milestone.id];
                                        const milestoneUrl = milestone.url || "";
                                        const { icon: PlatformIcon } = getIconForUrl(milestoneUrl);
                                        const socialTrackerColors = (challenge as ChallengeWithEntriesStyles)?.questsStyles?.cardTrackers?.find(t => t.subtype === "SOCIAL");
                                        return (
                                            <QuestCard
                                                key={index}
                                                refetch={refetch}
                                                refetchChallenge={refetch}
                                                label={milestone.name || "Complete the Challenge"}
                                                icon={<PlatformIcon size={24} />}
                                                type="outlined"
                                                milestone={milestone}
                                                isComplete={progression?.isComplete ?? false}
                                                isClaimed={!!(progression?.isComplete && progression?.claimedAt)}
                                                isAccepted={challenge?.isAccepted ?? false}
                                                challenge={challengeWithDates}
                                                onLoginRequired={handleLoginRequired}
                                                trackerColors={socialTrackerColors}
                                                brandNameOverride={hubBrandName}
                                                brandColorOverride={hubBrandColor}
                                            />
                                        );
                                    })}
                            </div>
                        }

                        {/* Referral quests - 2 column on desktop, 1 column on mobile */}
                        {referralMilestones.length > 0 &&
                            <div className="missions-section__referral-quests">
                                {referralMilestones
                                    .sort((a, b) => {
                                        const aClaimed = progressionMap[a.id]?.isComplete && progressionMap[a.id]?.claimedAt ? 1 : 0;
                                        const bClaimed = progressionMap[b.id]?.isComplete && progressionMap[b.id]?.claimedAt ? 1 : 0;
                                        return aClaimed - bClaimed;
                                    })
                                    .map((milestone, index) => {
                                        const progression = progressionMap[milestone.id];
                                        const referralTrackerColors = (challenge as ChallengeWithEntriesStyles)?.questsStyles?.cardTrackers?.find(t => t.subtype === "REFERRAL");
                                        return (
                                            <QuestCard
                                                key={index}
                                                refetch={refetch}
                                                refetchChallenge={refetch}
                                                label={milestone.name || "Refer a Friend"}
                                                icon={<FaUserPlus size={24} />}
                                                type="outlined"
                                                milestone={milestone}
                                                isComplete={progression?.isComplete ?? false}
                                                isClaimed={!!(progression?.isComplete && progression?.claimedAt)}
                                                isAccepted={challenge?.isAccepted ?? false}
                                                challenge={challengeWithDates}
                                                onLoginRequired={handleLoginRequired}
                                                onReferralClick={() => setShowInviteModal(true)}
                                                currentAmount={progression?.amount}
                                                targetAmount={milestone.targetAmount}
                                                trackerColors={referralTrackerColors}
                                                brandNameOverride={hubBrandName}
                                                brandColorOverride={hubBrandColor}
                                            />
                                        );
                                    })}
                            </div>
                        }

                        {/* Game quests - grouped by game with progress tracking */}
                        {hasGameMilestones &&
                            <div className="challenge-page_game-quests">
                                {Object.entries(gameMilestonesByGame).map(([gameId, milestones]) => {
                                    const gameTrackerColors = (challenge as ChallengeWithEntriesStyles)?.questsStyles?.cardTrackers?.find(t => t.subtype === "GAME");
                                    return (
                                        <GameQuestSection
                                            key={gameId}
                                            gameId={gameId}
                                            milestones={milestones}
                                            progressionMap={progressionMap}
                                            trackerColors={gameTrackerColors}
                                            refetch={refetch}
                                            isAccepted={challenge?.isAccepted ?? false}
                                            challenge={challenge}
                                            onLoginRequired={handleLoginRequired}
                                        />
                                    );
                                })}
                            </div>
                        }
                    </div>
                </div>

                {/* Explore More (Promotions) */}
                <ChallengePromotionsSection challenge={challenge} />

                {/* FAQ Section */}
                <ChallengeFAQ challenge={challenge} />
            </div>
        </section>
    );
};
